import React, { useEffect, useMemo, useState } from 'react';
import styled from 'libs/styled';
import Text from 'components/Text';
import { GetStockProducts } from '@fogia/core-web/libs/GrebbCommerceAPI/Products';
import { above } from '@fogia/core-web/utils/mediaqueries';
import Button from 'components/Button';

interface StockProduct {
    name: string;
    description: string;
    link: string | null;
    stockCount: number;
    image: string | null;
    productType: string;
}

const filterTypography = ['Primary/Mobile/13_100_510_-1', null, null, null, null, 'Primary/15_120_510_-1'] as Array<
    string | null
>;
const nameTypography = ['Primary/Mobile/13_100_510_-1', null, null, null, null, 'Primary/15_120_510_-1'] as Array<
    string | null
>;
const descriptionTypography = ['Secondary/Mobile/13_120_400', null, null, null, null, 'Secondary/15_120_400'] as Array<
    string | null
>;

const Wrapper = styled('div')`
    width: 100%;
    padding: 0 var(--grid-padding, 10px);
`;

const FilterBar = styled('div')`
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    padding-bottom: 30px;
`;

const FilterButton = styled('button')`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    color: ${({ active }: { active?: boolean }) => (active ? 'var(--ui-secondary-aa)' : 'var(--ui-primary)')};
`;

const FilterSeparator = styled('span')`
    padding: 0 6px;
`;

const ProductList = styled('div')`
    display: flex;
    flex-direction: column;
`;

const ProductListHeader = styled('div')`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    margin-bottom: 10px;

    ${above['tablet.lg']} {
        grid-template-columns: repeat(24, 1fr);
        margin-bottom: 20px;
    }
`;

const ProductListHeaderName = styled('div')`
    grid-column: span 7;

    ${above['tablet.lg']} {
        grid-column: span 12;
    }
`;

const ProductListHeaderStockCount = styled('div')`
    grid-column: 8 / 10;
    text-align: right;

    ${above['tablet.lg']} {
        grid-column: 19 / 22;
    }
`;

const ProductListHeaderImage = styled('div')`
    grid-column: 10 / 13;
    text-align: right;

    ${above['tablet.lg']} {
        grid-column: 22 / 25;
    }
`;

const ProductRow = styled('div')`
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    border-top: 1px solid rgba(23, 23, 23, 0.15);
    padding: 20px 0 40px;

    ${above['tablet.lg']} {
        grid-template-columns: repeat(24, 1fr);
    }
`;

const ProductLeft = styled('div')`
    display: flex;
    flex-direction: column;
    grid-column: span 7;

    ${above['tablet.lg']} {
        grid-column: span 12;
    }
`;

const StockCount = styled('div')`
    text-align: right;
    grid-column: 8 / 10;

    ${above['tablet.lg']} {
        grid-column: 19 / 22;
    }
`;

const ImageLinkContainer = styled('div')`
    grid-column: 10 / 13;
    text-align: right;

    ${above['tablet.lg']} {
        grid-column: 22 / 25;
    }
`;
const ImageLink = styled('a')``;

const StockStatus: React.FC = () => {
    const [products, setProducts] = useState<StockProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeFilter, setActiveFilter] = useState<string>('all');

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const data = await GetStockProducts();
                setProducts(data || []);
            } catch {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    const categories = useMemo(() => {
        const types = new Set(products.map((p) => p.productType));
        return Array.from(types).sort();
    }, [products]);

    const filteredProducts = useMemo(() => {
        if (activeFilter === 'all') {
            return products;
        }
        return products.filter((p) => p.productType === activeFilter);
    }, [products, activeFilter]);

    const handleFilterClick = (filter: string) => {
        setActiveFilter(filter);
    };

    if (loading) {
        return null;
    }

    if (error) {
        return null;
    }

    const filterItems = [{ label: 'All products', value: 'all' }, ...categories.map((c) => ({ label: c, value: c }))];

    return (
        <Wrapper>
            <FilterBar>
                {filterItems.map((filter, index) => (
                    <React.Fragment key={filter.value}>
                        {index > 0 && (
                            <FilterSeparator>
                                <Text typography={filterTypography}>,</Text>
                            </FilterSeparator>
                        )}
                        <FilterButton
                            active={activeFilter === filter.value}
                            onClick={() => handleFilterClick(filter.value)}
                        >
                            <Text typography={filterTypography}>{filter.label}</Text>
                        </FilterButton>
                    </React.Fragment>
                ))}
            </FilterBar>
            <ProductList>
                <ProductListHeader>
                    <ProductListHeaderName>
                        <Text typography="Primary/15_120_510_-1">Name</Text>
                    </ProductListHeaderName>
                    <ProductListHeaderStockCount>
                        <Text typography="Primary/15_120_510_-1">Quantity</Text>
                    </ProductListHeaderStockCount>
                    <ProductListHeaderImage>
                        <Text typography="Primary/15_120_510_-1">Media</Text>
                    </ProductListHeaderImage>
                </ProductListHeader>
                {filteredProducts.map((product, index) => (
                    <ProductRow key={`${product.name}-${product.description}-${index}`}>
                        <ProductLeft>
                            <Text typography={nameTypography}>{product.name}</Text>
                            <Text typography={descriptionTypography}>{product.description}</Text>
                        </ProductLeft>
                        <StockCount>
                            <Text typography={nameTypography}>{product.stockCount}</Text>
                        </StockCount>
                        <ImageLinkContainer>
                            {product.image && (
                                <Button to={product.image} size="small">
                                    Image
                                </Button>
                            )}
                        </ImageLinkContainer>
                    </ProductRow>
                ))}
            </ProductList>
        </Wrapper>
    );
};

export default StockStatus;
